//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IMRToolbarOptions } from '@imr/controls.angularjs/components/toolbar';

//#endregion

import { IMRBaseControlController } from '../../base'

import { Enums } from '../../../../models/common'
import { IOpObject, IReferenceType } from '../../../../models/util/interfaces'

import { UserSessionService } from '../../../../services/user-session'
import { TypeHelper, LocalizationHelper, TemplateHelper, ConfigHelper, LogHelper, ConvertHelper } from '../../../../helpers'
import { Dictionary, KeyType, KeyValuePair } from '../../../../helpers/dictionary'

import { GridOptions, GridController } from '../../grid'
import { IMRSelectGridInputFieldOptions, IIMRSelectGridInputField, COMPONENT_NAME as SELECT_GRID_INPUT_FIELD_COMPONENT_NAME, IMRSelectGridInputFieldInitializedArgs } from '../../select-grid/input-field'

import { IMREditGridOptions, IIMREditGrid } from './edit-grid.interfaces'
import { IMRSelectGridObjectsSelected } from '../../select-grid'

export class IMREditGridController<TSourceItem, TTargetItem>
    extends
    IMRBaseControlController<IIMREditGrid<TSourceItem, TTargetItem>, IMREditGridOptions<TSourceItem, TTargetItem>>
    implements
    IIMREditGrid<TSourceItem, TTargetItem> {

    //#region Consts

    private get LOG_PREFIX(): string { return '[SIMAX.Common.EditGrid]' }

    //#endregion


    //#region Members

    public isLoading: boolean

    private isAllowDuplicates: boolean
    private isGridEditing: boolean

    //#endregion


    //#region Ctor

    public constructor(private userSessionService: UserSessionService) {
        super()
    }

    //#endregion


    //#region Lifecycle

    protected initialize(options: IMREditGridOptions<TSourceItem, TTargetItem>): JQueryPromise<void> {
        this.isLoading = true
        this.setDefaults()
        this.setOptions()

        const toInitialize = []

        if (this.isAllowEditing) {
            toInitialize.push(this.initializeSelectGridPopover(), this.initializeBottomToolbar())
        }

        toInitialize.push(this.initializeGridOptions())

        return $
            .when(...toInitialize)
            .then(() => {
                this.isLoading = false
            })
    }

    private setDefaults(): void {
        this.gridOptions = null
        this.isAllowDuplicates = false
        this.isAllowEditing = true
        this.isGridEditing = false
        this.selectGridInputFieldOptions = null

        this.addedObjects = []
        this.modifiedObjects = []
        this.deletedObjects = []

        this.originalObjectsHashDict = null
        this.currentNewObjectId = -1

        this.updateToolbarButtons()
    }

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.allowEditing != null) {
                this.isAllowEditing = this.options.allowEditing
            }

            if (this.options.allowDuplicates != null) {
                this.isAllowDuplicates = this.options.allowDuplicates
            }
        }
    }

    //#endregion


    //#region IIMREditGrid Implementation

    public getTargetGrid(): GridController<TTargetItem> {
        return this.grid
    }

    public getSourceGrid(): GridController<TSourceItem> {
        return this.selectGridInputField.selectGridPopover.getSelectGrid().getGrid()
    }

    public getSelectGridInputField(): IIMRSelectGridInputField<TSourceItem> {
        return this.selectGridInputField
    }

    public hasChanges(): boolean {
        return _.size(this.addedObjects) > 0 || _.size(this.deletedObjects) > 0 || _.size(this.modifiedObjects) > 0
    }

    public reset(): void {
        this.addedObjects = []
        this.modifiedObjects = []
        this.deletedObjects = []

        this.originalObjectsHashDict = null
        this.currentNewObjectId = -1

        this.updateToolbarButtons()

        if (this.grid != null) {
            this.grid.clearPrependedItems()
            this.grid.clearItemDisplayStates()
            this.grid.refresh()
        }

        super.callEvent(this.options.onReset)
    }

    public updateItem(object: TTargetItem, editedData: any): void {
        let editedObject: TTargetItem = object

        if (this.options != null && _.isFunction(this.options.onBeforeEdit)) {
            editedObject = this.options.onBeforeEdit({
                component: this,
                object: editedObject,
                editedData
            })
        }

        if (this.getOriginalObjectHash(editedObject) === this.getObjectHash(editedObject)) {
            this.deleteModifiedObject(editedObject)
        } else {
            this.addModifiedTargetObject(editedObject)
        }

        this.updateToolbarButtons()
    }

    //#endregion


    //#region Adding

    private addedObjects: TTargetItem[]
    private currentNewObjectId: number = -1

    private addSourceObjects(objects: TSourceItem[]) {
        const targetObjects: TTargetItem[] = this.convertSourceObjectsToTargetObjects(objects)

        _.forEach(targetObjects, (targetObject: TTargetItem) => {
            this.addTargetObject(targetObject)
        })

        this.updateToolbarButtons()
        this.grid.refresh()
    }

    private addTargetObject(object: TTargetItem): void {
        if (
            this.isAllowDuplicates
            || (!this.isTargetObjectAdded(object) && !this.gridContainsTargetObject(object))
        ) {
            this.setNewTargetObjectId(object)
            this.addedObjects.push(object)
            this.grid.prependItem(object)
            this.grid.setItemDisplayState(object, Enums.OpChangeState.New)
        }
    }

    private setNewTargetObjectId(object: TTargetItem): void {
        if (this.getObjectId(object) == null) {
            this.setObjectId(object, this.currentNewObjectId)
            this.currentNewObjectId -= 1
        }
    }

    private isTargetObjectAdded(object: TTargetItem): boolean {
        return _.find(this.addedObjects, (addedObject: TTargetItem) => {
            return this.objectEquals(addedObject, object)
        }) != null
    }

    //#endregion


    //#region Deleting

    private deletedObjects: TTargetItem[]

    private deletedTargetObjects(objects: TTargetItem[]): void {
        _.forEach(objects, (object: TTargetItem) => {
            this.deleteTargetObject(object)
        })

        this.updateToolbarButtons()
        this.grid.refresh()
    }

    private deleteTargetObject(object: TTargetItem): void {
        if (this.isTargetObjectAdded(object)) {
            this.deleteAddedTargetObject(object)
        }
        else if (this.isTargetObjectDeleted(object)) {
            this.unDeleteTargetObject(object)
        }
        else {
            this.deletedObjects.push(object)
            this.grid.setItemDisplayState(object, Enums.OpChangeState.Delete)
        }
    }

    private deleteAddedTargetObject(object: TTargetItem): void {
        this.addedObjects = _.filter(this.addedObjects, (addedObject: TTargetItem) => {
            return !this.objectEquals(addedObject, object)
        })

        this.grid.removePrependedItem(object)
    }

    private isTargetObjectDeleted(object: TTargetItem): boolean {
        return _.find(this.deletedObjects, (deletedObject: TTargetItem) => {
            return this.objectEquals(deletedObject, object)
        }) != null
    }

    private unDeleteTargetObject(object: TTargetItem): void {
        this.deletedObjects = _.filter(this.deletedObjects, (deletedObjects: TTargetItem) => {
            return !this.objectEquals(deletedObjects, object)
        })

        if (this.isTargetObjectModified(object)) {
            this.grid.setItemDisplayState(object, {
                opState: Enums.OpChangeState.Modified
            })
        } else {
            this.grid.setItemDisplayState(object, {
                opState: Enums.OpChangeState.Loaded
            })
        }
    }

    //#endregion


    //#region Editing

    private modifiedObjects: TTargetItem[]

    private addModifiedTargetObject(object: TTargetItem): void {
        if (!this.isTargetObjectAdded(object) && !this.isTargetObjectModified(object)) {
            this.modifiedObjects.push(object)

            if (!this.isTargetObjectDeleted(object)) {
                this.grid.setItemDisplayState(object, Enums.OpChangeState.Modified)
            }
        }

        if (this.isTargetObjectAdded(object)) {
            const obj = _.find(this.addedObjects, (addedObject: TTargetItem) => {
                return this.getObjectId(addedObject) === this.getObjectId(object)
            })

            const objectIndex: number = _.indexOf(this.addedObjects, obj)

            this.addedObjects[objectIndex] = object
            this.grid.replacePrependedItem(obj, object)
        }

        this.grid.refresh()
    }

    private deleteModifiedObject(object: TTargetItem): void {
        this.modifiedObjects = _.filter(this.modifiedObjects, (modifiedObject: TTargetItem) => {
            return !this.objectEquals(modifiedObject, object)
        })

        if (!this.isTargetObjectDeleted(object)) {
            this.grid.setItemDisplayState(object, Enums.OpChangeState.Loaded)
        }

        this.grid.refresh()
    }

    private isTargetObjectModified(object: TTargetItem): boolean {
        return _.find(this.modifiedObjects, (modifiedObject: TTargetItem) => {
            return this.objectEquals(modifiedObject, object)
        }) != null
    }

    //#endregion


    //#region Hash

    private originalObjectsHashDict: Dictionary<any>

    private createOriginalHashDict(objects: TTargetItem[]): Dictionary<string> {
        const pairs: KeyValuePair<string>[] = _.map(objects, (object: TTargetItem) => {
            return new KeyValuePair(this.getObjectId(object), ConvertHelper.hash(object))
        })

        return new Dictionary(pairs, KeyType.Number)
    }

    private getObjectHash(object: TTargetItem): string {
        return ConvertHelper.hash(object)
    }

    private getOriginalObjectHash(object: TTargetItem): string {
        return this.originalObjectsHashDict.tryGetValue(this.getObjectId(object))
    }

    private updateHashDict(objects: TTargetItem[]): void {
        if (this.originalObjectsHashDict == null) {
            this.originalObjectsHashDict = this.createOriginalHashDict(objects)
        }
    }

    //#endregion


    //#region Objects

    private getObjectId(object: TTargetItem): number {
        if (object != null) {
            if (_.isFunction(this.options.getObjectId)) {
                return super.callEvent(this.options.getObjectId, {
                    object
                }) as number
            }

            if (TypeHelper.isIOpObject(object)) {
                return (object as any as IOpObject).idObject
            }

            if (TypeHelper.isIReferenceType(object)) {
                return (object as any as IReferenceType).getReferenceKey()
            }

            LogHelper.warn(this.LOG_PREFIX, 'Unable to determine target object id. Not an IReferenceType nor an IOpObject and id function was not specified.')
        }

        return null
    }

    private setObjectId(object: TTargetItem, id: number): void {
        if (object != null) {
            if (_.isFunction(this.options.setObjectId)) {
                super.callEvent(this.options.setObjectId, {
                    object,
                    id
                })
            }
            else if (TypeHelper.isIOpObject(object)) {
                (object as any as IOpObject).idObject = id
            }
            else {
                LogHelper.warn(this.LOG_PREFIX, 'Unable to set target object id. Not an IOpObject and id function was not specified.')
            }
        }
    }

    private objectEquals(object1: TTargetItem, object2: TTargetItem): boolean {
        return this.getObjectId(object1) === this.getObjectId(object2)
    }

    private convertSourceObjectsToTargetObjects(objects: TSourceItem[]): TTargetItem[] {
        if (_.isFunction(this.options.onBeforeAdd)) {
            const onBeforeAddResult = this.options.onBeforeAdd({
                component: this,
                objects
            })

            return onBeforeAddResult as TTargetItem[]
        }

        return objects as any as TTargetItem[]
    }

    //#endregion


    //#region Select Grid Popover

    private selectGridInputField: IIMRSelectGridInputField<TSourceItem>
    public selectGridInputFieldOptions: IMRSelectGridInputFieldOptions<TSourceItem>

    private initializeSelectGridPopover(): JQueryPromise<any> {
        if (this.options.selectGridOptions != null) {
            this.selectGridInputFieldOptions = this.options.selectGridOptions
        }

        this.selectGridInputFieldOptions = _.assign(this.selectGridInputFieldOptions || {}, {
            customButtonTemplate: TemplateHelper.getIcon(ConfigHelper.Icon.ARROW_DROP_UP, this.userSessionService)
        })

        this.selectGridInputFieldOptions.selectGridPopoverOptions = _.assign(this.selectGridInputFieldOptions.selectGridPopoverOptions || {}, {
            closeAfterSelect: true,
            preserveSelectionOnClose: true,
        })

        this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions = _.assign(this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions || {}, {
            multiSelect: true,
            required: true,
            clearSelectionAfterSelect: true,
        })

        if (this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.gridOptions == null) {
            this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.gridOptions = this.options.sourceGridOptions
        }

        this.selectGridInputFieldOptions.onDisposed = TypeHelper.wrapAndCall(this.selectGridInputFieldOptions.onDisposed, () => {
            this.selectGridInputFieldOptions = null
        })

        this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.onObjectsSelected
            = TypeHelper.wrapAndCall(this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.onObjectsSelected, (e: IMRSelectGridObjectsSelected<TSourceItem>) => {
                this.onSelectGridObjectsSelected(e.selectedObjects)
            })

        if (this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.gridOptions != null) {
            this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.gridOptions.onSelectionChanged
                = TypeHelper.wrapAndCall(this.selectGridInputFieldOptions.selectGridPopoverOptions.selectGridOptions.gridOptions.onSelectionChanged, (e: any) => {
                    this.onSelectGridSelectionChanged(e)
                })
        }

        return super
            .waitForOnInitialized(this.selectGridInputFieldOptions)
            .then((e: IMRSelectGridInputFieldInitializedArgs<TSourceItem>) => {
                this.selectGridInputField = e.component
            })
    }

    public onSelectGridSelectionChanged(e: any): void {
        this.updateToolbarButtons()
    }

    private onSelectGridObjectsSelected(objects: TSourceItem[]): void {
        this.addSourceObjects(objects)
    }

    //#endregion


    //#region Submit 

    private prepareModifiedObjectsToSubmit(): TTargetItem[] {
        return _.filter(this.modifiedObjects, (modifiedObject: TTargetItem) => {
            return !this.isTargetObjectDeleted(modifiedObject)
        })
    }

    private submitChanges(): void {
        const submitResult: any = this.callEvent(this.options.onSubmit, {
            addedObjects: TypeHelper.clone(this.addedObjects),
            modifiedObjects: this.prepareModifiedObjectsToSubmit(),
            deletedObjects: TypeHelper.clone(this.deletedObjects)
        })

        if (submitResult != null) {
            if (TypeHelper.isPromise<void>(submitResult)) {
                this.isLoading = true;

                (submitResult as JQueryPromise<void>)
                    .always(() => {
                        this.isLoading = false
                    })
            }
        }
    }

    //#endregion


    //#region Grid

    private grid: GridController<TTargetItem>
    public gridOptions: GridOptions

    private gridContainsTargetObject(object: TTargetItem): boolean {
        return this.grid.containsItem(object)
    }

    private onGridDataRaedy(data: TTargetItem[]): void {
        this.updateHashDict(data)
    }

    private onGridEditingStart(e: any): void {
        if (e.column.dataType !== 'boolean') {
            this.isGridEditing = true
            this.updateToolbarButtons()
        }
    }

    private onGridEditorFocusOut(): void {
        this.isGridEditing = false
        this.updateToolbarButtons()
    }

    private onGridRowUpdating(e: any): void {
        const editedData: any = e.newData
        let targetObject: TTargetItem = this.grid.getSourceObject(e.key)

        this.updateItem(targetObject, editedData)
        this.updateToolbarButtons()
    }

    private onGridSelectionChanged(e: any): void {
        this.updateToolbarButtons()
    }

    private onGridEditorPreparing(e: any): void {
        if (e.parentType === 'dataRow' && e.editorOptions != null) {
            e.editorOptions.onFocusOut = TypeHelper.wrapAndCall(e.editorOptions.onFocusOut, () => {
                this.onGridEditorFocusOut()
            })
        }
    }

    private initializeGridOptions(): JQueryPromise<any> {
        if (this.options.targetGridOptions) {
            this.gridOptions = this.options.targetGridOptions
        }

        if (this.gridOptions == null) {
            this.gridOptions = {} as GridOptions
        }

        if (this.options.getObjectId != null) {
            this.gridOptions.getDataSourceKey = (object: TTargetItem) => {
                return super.callEvent(this.options.getObjectId, {
                    object
                })
            }
        }

        this.gridOptions.onInitialized = TypeHelper.wrapAndCall(this.gridOptions.onInitialized, (e: any, grid: GridController<TTargetItem>) => {
            this.grid = grid
        })

        this.gridOptions.onDisposed = TypeHelper.wrapAndCall(this.gridOptions.onDisposed, () => {
            this.grid = null
        })

        this.gridOptions.onDataReady = TypeHelper.wrapAndCall(this.gridOptions.onDataReady, (data: TTargetItem[]) => {
            this.onGridDataRaedy(data)
        })

        this.gridOptions.onEditingStart = TypeHelper.wrapAndCall(this.gridOptions.onEditingStart, (e: any) => {
            this.onGridEditingStart(e)
        })

        this.gridOptions.onEditorPreparing = TypeHelper.wrapAndCall(this.gridOptions.onEditorPreparing, (e: any) => {
            this.onGridEditorPreparing(e)
        })

        this.gridOptions.onRowUpdating = TypeHelper.wrapAndCall(this.gridOptions.onRowUpdating, (e: any) => {
            this.onGridRowUpdating(e)
        })

        this.gridOptions.onSelectionChanged = TypeHelper.wrapAndCall(this.gridOptions.onSelectionChanged, (e: any) => {
            this.onGridSelectionChanged(e)
        })

        return super.waitForOnInitialized(this.gridOptions)
    }

    //#endregion


    //#region Bottom Toolbar

    public isAllowEditing: boolean
    public bottomToolbarOptions: IMRToolbarOptions

    private deleteButton: DevExpress.ui.dxButton
    private submitButton: DevExpress.ui.dxButton

    private initializeBottomToolbar(): any {
        this.bottomToolbarOptions = {
            items: [{
                location: 'after',
                widget: 'dxButton',
                options: {
                    disabled: true,
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.REMOVE, this.userSessionService),

                    onInitialized: (e: any) => {
                        this.deleteButton = e.component
                    },

                    onDisposing: () => {
                        this.deleteButton = null
                    },

                    onClick: () => {
                        this.deletedTargetObjects(this.grid.selectedItems)
                    }
                }
            }, {
                location: 'after',
                widget: 'dxButton',
                options: {
                    disabled: true,
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Apply),

                    onInitialized: (e: any) => {
                        this.submitButton = e.component
                    },

                    onDisposing: () => {
                        this.submitButton = null
                    },

                    onClick: () => {
                        this.submitChanges()
                    }
                }
            }],
        }

        return super.waitForOnInitialized(this.bottomToolbarOptions)
    }

    private updateToolbarButtons(): void {
        if (this.deleteButton != null && this.grid != null) {
            const isDeleteButtonDisabled: boolean = this.grid.selectedCount === 0

            this.deleteButton.option('disabled', isDeleteButtonDisabled)
        }

        if (this.submitButton != null) {
            const isSumbitButtonDisabled: boolean = !this.hasChanges() || this.isGridEditing

            this.submitButton.option('disabled', isSumbitButtonDisabled)
        }
    }

    //#endregion

}
